<template>
  <div
    class="program prog"
    :class="StatusClass"
  >
    <div class="program-wrapper">
      <div class="program__header">
        <h3
          title="Program Name"
          class="program__name"
        >
          {{ Data.name }}
        </h3>
        <button
          :class="{'program__edit-button--disabled': !computedEnableEdit}"
          :disabled="!computedEnableEdit"
          :title="!computedValidIrrigation ? $t('messages.sharedTexts.programs.editDisabledIrrigationType'): 'Edit this programs parameters'"
          class="program__edit-button g-btn g-btn--rounded"
          @click="EditClick()"
        >
          {{ $t('messages.sharedTexts.controls.edit') }}
          <font-awesome-icon :icon="['fas', 'expand-arrows-alt']" />
        </button>
      </div>

      <div class="program__body">
        <div class="program__body-wrapper">
          <div class="program__body__days-wrapper">
            <div
              class="program__body__intelligence-wrapper"
              title="Not available"
            >
              <ol class="program__body__intelligence-levels">
                <li
                  v-for="(level, index) in IntelligenceLevels"
                  :key="index"
                  class="program__body__intelligence-level disabled"
                  :class="['program__body__intelligence-level--'+level.className]"
                >
                  {{ level.name }}
                </li>
              </ol>
            </div>

            <div class="program__body__days">
              <div
                v-for="(day, index) in Data.fullDaySchedule.slice(0, 7)"
                ref="day"
                :key="index"
                class="program__body__day"
                @click="DayClicked(day, index)"
              >
                <span
                  class="program__body__day__name"
                  v-html="(!day.today) ? day.name : 'Today'"
                />
                <div
                  class="program__body__day__circle"
                  :class="{'active': (day !== '-')}"
                />
              </div>
            </div>
          </div>
          <div class="program__body__information">
            <p
              title="Status"
              class="g-capitalize"
            >
              <font-awesome-icon :icon="['far', 'clock']" />
              <slot v-if="!StatusText.match(/\d/g)">
                {{ computedGetCamelStatus }}
              </slot>
              <slot v-else>
                {{ StatusText }}
              </slot>
            </p>
            <slot v-show="Data.runnable">
              <p class="program__body__information__text">
                <font-awesome-icon :icon="['custom', 'station']" />
                {{ RunningStations }} {{ $t('messages.sharedTexts.outOf') }} {{ Data.noOfStation }} {{ $t('messages.sharedTexts.status.running') }}
              </p>
            </slot>
          </div>
        </div>

        <div class="program__controls">
          <button
            v-if="Number(ControllerMode) === 0 || Number(ControllerMode) > 2 || Data.ManRunnable === false"
            type="button"
            :title="$t('messages.sharedTexts.programs.cannotRun')"
            class="g-btn g-btn--round program__controls__run-control disabled"
          >
            <font-awesome-icon
              title="Disabled"
              :icon="['fas', 'times']"
            />
          </button>
          <button
            v-else-if="Data.runningEntries.length >= 1 && CountRunningEntries > 0"
            type="button"
            class="g-btn g-btn--round program__controls__run-control program__controls__run-control--pause"
            @click="OpenRegulateProgramModal()"
          >
            <svg
              class="radial-range"
              viewBox="0 0 120 120"
            >
              <circle
                class="radial-range__background"
                cx="50%"
                cy="50%"
                r="54"
                fill="none"
                stroke="#e6e7e8"
                stroke-width="12"
              />
              <circle
                class="radial-range__ring"
                cx="50%"
                cy="50%"
                r="54"
                fill="none"
                :stroke="(StatusText === 'Paused' || OverrideStatusText === 'Resuming') ? '#999' : '#3565b0'"
                stroke-width="12"
                :style="{strokeDashoffset: GetLongestRadialProgress}"
              />
            </svg>

            <font-awesome-icon
              title="Pause Program"
              :icon="['fas', 'pause']"
            />
          </button>
          <button
            v-else-if="(Data.runnable && Data.status === '0') || (Data.runnable && Data.status === '1') || (Data.runnable && Data.status === '2')"
            type="button"
            title="Manually Start/Stop a Program"
            class="g-btn g-btn--round program__controls__run-control program__controls__run-control--play"
            @click="RegulateProgram('Start')"
          >
            <font-awesome-icon :icon="['fas', 'play']" />
          </button>
          <button
            v-else
            type="button"
            :title="$t('messages.sharedTexts.programs.cannotRun')"
            class="g-btn g-btn--round program__controls__run-control program__controls__run-control--times"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
      </div>
    </div>

    <div
      ref="ProgramOptions"
      class="program__options"
      :class="{'open': OptionsToggleStatus}"
    >
      <div class="program__options-wrapper">
        <slot v-if="Data.runningEntries && Data.runningEntries.length">
          <div
            v-for="runningEntry in Data.runningEntries"
            :key="runningEntry.ID"
            class="program__options__option"
          >
            <div class="program__options__option__information">
              <div class="program__options__option__entry">
                <slot v-if="maxSteps === 1">
                  {{ $tc('messages.sharedTexts.stations.station', 1) }}
                </slot>
                <slot v-else>
                  {{ $tc('messages.sharedTexts.step', 0) }}
                </slot>
                <slot v-if="StatusText !== 'Soaking'">
                  {{ runningEntry.currentStep }}/{{ runningEntry.totalStep }}
                </slot>
                <progress
                  :value="(!isNaN(runningEntry.currentStep)) ? runningEntry.currentStep : 0"
                  :max="(!isNaN(runningEntry.totalStep)) ? runningEntry.totalStep : 0"
                />
              </div>

              <div class="program__options__option__entry">
                <slot v-if="runningEntry.currentCycle =='-' || runningEntry.currentCycle === null || runningEntry.currentCycle < 1">
                  <span>
                    {{ $tc('messages.sharedTexts.cycle', 0) }} -/-
                  </span>
                  <progress
                    value="0"
                    max="1"
                    disabled
                  />
                </slot>
                <slot v-else>
                  <span>
                    {{ $tc('messages.sharedTexts.cycle', 0) }} {{ (!isNaN(runningEntry.currentCycle)) ? runningEntry.currentCycle : 0 }} / {{ runningEntry.totalCycles }}
                  </span>
                  <progress
                    :value="(!isNaN(runningEntry.currentCycle)) ? runningEntry.currentCycle : 0"
                    :max="(!isNaN(runningEntry.totalCycles)) ? runningEntry.totalCycles : 0"
                  />
                </slot>
              </div>
              <div class="program__options__option__entry">
                <span>
                  {{ $t('messages.sharedTexts.status.status') }}
                </span>
                <span>
                  {{ computedGetCamelStatus }}
                </span>
              </div>
              <div
                v-if="runningEntry.runningStations.length !== 0"
                class="program__options__option__entry"
              >
                <span>{{ $t('messages.sharedTexts.programs.includedStations') }}</span>
                <div class="program__options__option__entry__stationlist">
                  <select
                    :v-model="Selected"
                    :disabled="runningEntry.runningStations.length === 0"
                    @click="Change($event)"
                    @change="Change($event)"
                  >
                    <option
                      v-for="(runningStation) in runningEntry.runningStations"
                      :key="runningStation.id"
                      :value="(runningStation.name !== '') ? runningStation.name : runningStation.id"
                    >
                      {{ (runningStation.name === '') ? runningStation.id : runningStation.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="program__controls">
              <div
                v-if="Data.prgDetailBtnName !== 'Resume'"
                class="program__controls__clock"
              >
                {{ formatClock(runningEntry.totalRunningTime - getRunningEntryElapsedTime(runningEntry)) }}
              </div>
              <div
                v-else
                class="program__controls__clock"
              >
                {{ formatClock((runningEntry.totalRemainTime)) }}
              </div>
              <button
                v-if="Data.runningEntries.length >= 1 && runningEntry.runningMode !== 'Paused'"
                type="button"
                class="g-btn g-btn--round program__controls__run-control program__controls__run-control--pause"
                @click="OpenRegulateProgramModal(true, runningEntry)"
              >
                <svg
                  class="radial-range"
                  viewBox="0 0 120 120"
                >
                  <circle
                    class="radial-range__background"
                    cx="50%"
                    cy="50%"
                    r="54"
                    fill="none"
                    stroke="#e6e7e8"
                    stroke-width="12"
                  />
                  <circle
                    class="radial-range__ring"
                    cx="50%"
                    cy="50%"
                    r="54"
                    fill="none"
                    stroke="#3565b0"
                    stroke-width="12"
                    :style="{strokeDashoffset: getRunningEntryRadialProgress(runningEntry)}"
                  />
                </svg>
                <font-awesome-icon :icon="['fas', 'pause']" />
              </button>
              <button
                v-else
                type="button"
                class="g-btn g-btn--round program__controls__run-control program__controls__run-control--play"
                @click="OpenRegulateProgramModal(true, runningEntry)"
              >
                <font-awesome-icon :icon="['fas', 'play']" />
              </button>
            </div>
          </div>
        </slot>
        <p
          v-else
          class="g-text--center"
        >
          <slot v-if="Data.runnable">
            {{ computedStatus }}
          </slot>
          <slot v-else>
            {{ $t('messages.sharedTexts.status.unableToRun', {item: $tc('messages.sharedTexts.programs.program', 1)}) }}
          </slot>
        </p>

        <div
          class="program__options__toggle-button"
          @click="OptionsToggleClick()"
        >
          <div class="program__options__toggle-button-wrapper">
            <font-awesome-icon :icon="['fas', 'angle-down']" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {SocketHandler} from '@/assets/javascript/mixins/Mixins'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {secondsToObj} from '@libs/Time-lib'
import moment from 'moment'
import $ from 'jquery'
import _ from 'lodash'

export default {
  name: 'ProgramComponent',
  components:
  {
    'font-awesome-icon': FontAwesomeIcon
  },
  mixins: [SocketHandler],
  props:
  {
    store:
    {
      type: Object,
      default: function() {
        return{}
      }
    },
    SocketMessage: {
      type: Object,
      default: function() {
        return{}
      }
    },
    PageMessage: {
      type: Object,
      default: function() {
        return{}
      }
    },
    ControllerGroup: {
      type: String,
      default: null
    },
    rainshutdown: {
      type: Boolean,
      default: null
    },
    RemainTimeToSubtract: {
      type: Number,
      default: 0
    },
    ControllerMode: {
      type: Number,
      default: null
    },
    Data: {
      type: Object,
      default: function() {
        return{
          ID: '1',
          name: 'Program 1',
          status: '1',
          waterBudget: '100',
          fullDaySchedule: 'WTFsSM-WTFsSMt',
          allDaysArranged: 'MtOTFsSMtOTFsS',
          runningStatus: 'Not running',
          nextRun: -1,
          noOfStation: '2',
          runningEntries: [],
          runnable: true,
          prgTopBtnName: 'Start Program',
          Message: '',
          NextRunConverted: 'date',
          rainshutdown: false
        }
      }
    }
  },
  data() {
    return{
      maxSteps: null,
      OverrideStatusText: '',
      OptionsToggleStatus: false,
      IntelligenceLevel: null,
      IntelligenceLevels: [{
        className: 'weather'
      }, {
        className: 'wind'
      }, {
        className: 'et-auto'
      }, {
        className: 'sensor'
      }],
      Granularity: 100,
      TimeInPercentage: 0,
      ShowAll: false,
      Selected: {
        Selected: '',
        SelectedData: ''
      },
      SelectedStationData: {}
    }
  },
  computed: {
    computedValidIrrigation() {
      if(!(this.Data.capabilities && this.Data.capabilities.programTypes)) return false
      return !!this.Data.capabilities.programTypes.find(type => parseInt(type.value) === parseInt(this.Data.irrigationType))
    },
    computedEnableEdit() {
      return !!(this.store.getData('user.access.programEdit') && this.store.getData('capabilities')) && this.computedValidIrrigation
    },
    computedGetCamelStatus() {
      const rtnString = typeof this.StatusText === 'string' ?  this.$t(`messages.sharedTexts.status.${_.camelCase(this.StatusText)}`) : ''
      return rtnString
    },
    computedStatus() {
      const isStarting = this.OverrideStatusText === 'Starting'
      let msg = this.$tc('messages.sharedTexts.programs.program', 1) + ' '
      msg += isStarting ? this.$t('messages.sharedTexts.status.' + this.OverrideStatusText.toLowerCase()) : this.$t('messages.sharedTexts.status.notRunning')

      return msg
    },
    computedRunningEntries() {
      return this.Data.runningEntries
    },
    CountRunningEntries() {
      let Count = 0
      this.Data.runningEntries.forEach((item, index) => {
        if(item.runningMode !== 'Paused')
          Count += 1
      })
      return Count
    },
    Update() {
      return this.Data.runningEntries
    },
    GetLongestRadialProgress() {
      if(this.Data.runningEntries.length) {
        // find longest running entry
        const longestTime = Math.max.apply(Math, this.Data.runningEntries.map(runningEntry => runningEntry.totalRemainTime))
        let longestRunningEntry = this.Data.runningEntries.find(runningEntry => runningEntry.totalRemainTime === longestTime)

        return this.getRunningEntryRadialProgress(longestRunningEntry)
      }
      return 0
    },
    StatusText() {
      if(this.OverrideStatusText !== '') {
        return this.OverrideStatusText
      }

      if(this.Data.Message !== '') {
        return this.Data.Message
      } else if(this.Data.runningMode === 'Pause') {
        return this.Data.runningMode
      } else if (this.Data.runningEntries.length > 0) {
        return this.Data.runningEntries[0].runningMode
      } else if(this.Data.runningMode === 'Paused') {
        return this.Data.runningMode
      } else if(this.Data.nextRun === -1) {
        return 'Not running'
      } else if(
        this.Data.nextRun !== undefined &&
        (this.Data.runningStatus === '' || this.Data.runningStatus === 'Not running') &&
        this.Data.nextRun !== '' && this.Data.status !== '0') {
        return moment(parseInt(this.Data.nextRun, 10)).utc().format(this.store.getData('settings.time.currentFormat'))
      } else {
        if((this.Data.status !== '0')) {
          return this.Data.runningStatus
        } else if(this.Data.runnable) {
          return 'Passive'
        } else {
          return 'Passive' // Cannot run, no special name for now
        }
      }
    },
    StatusClass() {
      const prefix = 'program--'
      let status = ''
      if(!this.Data.runnable && !this.Data.Configured) {
        status = 'not-runnable'
      } else if(this.Data.status !== '0' || (this.Data.status === '0' && this.Data.runningStatus.toLowerCase() !== 'not running')) {
        const test = this.Data.runningStatus.toLowerCase().split(' (')[0]

        /* eslint-disable */
        switch(test)
        {
          case 'running':
          case 'manually started':
          case 'auto started':
          case 'parallel run': status = 'running';
            break;
          case 'not running': status = 'active';
            break;
          default: status = 'inactive';
            break;
        }
        /* eslint-enable */
      } else {
        if((this.Data.status !== '0')) {
          return this.Data.runningStatus
        } else if(this.Data.runnable) {
          status = 'passive'
        }
      }
      
      if(this.Data.status === '2') {
        status = 'linked'
      }

      return prefix + status
    },
    RunningStations() {
      if(this.Data.runningEntries && this.Data.runningEntries.length > 0) {
        let counter = 0
        this.Data.runningEntries.forEach((entry) => {
          counter += entry.runningStations.length
        })
        return counter
      } else {
        return 0
      }
    },
    HasPausedEntries() {
      return ((this.Data.runningEntries.filter(entry => entry.runningMode === 'Paused').length) > 0)
    },
    rainshutdownMessage() {
      // `this` points to the vm instance
      return this.rainshutdown
    }
  },
  watch: {
    Data: {
      handler() {
        this.SelectedData = this.DefaultOptionSelect(this.Selected, this.Data)
      },
      deep: true
    },
    Update() {
      this.OverrideStatusText = ''
      setTimeout(() => {
        this.SetLayout()
      }, 0)
    },
    OptionsToggleStatus() {
      this.SetLayout()
    },
    PageMessage(Message) {
      if(Message.target === this.$options.name && Message.data === this.Data.ID) {
        this.RegulateProgram(Message.command, Message.entry)
      }
    }
  },
  mounted() {
    const capabilities = this.store.getData('capabilities')
    this.maxSteps = (capabilities) ? capabilities.steps.maxStationsPerStep : null
    this.$emit('programLoaded', this.Data.ID)
    this.SelectedData = this.DefaultOptionSelect(this.Selected, this.Data)
  },
  methods: {
    getRunningEntryElapsedTime(runningEntry) {
      const runTime = parseInt(runningEntry.totalRunningTime)
      const remainTime = parseInt(runningEntry.totalRemainTime)
      let entryTime = runningEntry.entryTime

      if((entryTime === undefined || runningEntry.oldTime === undefined) || remainTime < runningEntry.oldTime) {
        runningEntry.entryTime = this.store.getData('controller.clientModifiedTime')
      }

      const elapsedTime = (this.store.getData('controller.clientModifiedTime') - runningEntry.entryTime) / 1000
      const deltaTime = runTime - remainTime + elapsedTime

      runningEntry.oldTime = remainTime
      return deltaTime
    },
    getRunningEntryRadialProgress(runningEntry) {
      if(!_.isEmpty(runningEntry)) {
        const runTime = parseInt(runningEntry.totalRunningTime)
        const deltaTime = this.getRunningEntryElapsedTime(runningEntry)
        const percentageCompleted = deltaTime / runTime * 100
        const result = 339.292 - ((339.292 / this.Granularity) * percentageCompleted )

        return Math.abs(result)
      }
      return 0
    },
    OpenRegulateProgramModal(EntryState, Entry) {
      this.$emit('SetPageMessage', {
        target: 'ShowModal',
        modalData: {
          component: 'RegulateProgram',
          data: this.Data,
          entryState: EntryState,
          entry: Entry,
          selSt: this.SelectedData
        }
      })
    },
    RegulateProgram(Command, Entry) {
      if(Command === 'Start' || Command === 'Start new') {
        if(this.StatusText === 'Paused') return this.OpenRegulateProgramModal()

        this.SocketMessageSend({
          component: 'Programs',
          command: 'Start',
          id: this.Data.ID,
          startStepSt: 'Step to start from '
        })
        this.OverrideStatusText = 'Starting'
      } else if(Command === 'Stop') {
        this.StopEntries(Entry)
        this.OverrideStatusText = 'Stopping'
      } else if(Command === 'Pause' || Command === 'Resume') {
        this.PauseOrResumeEntries(Entry)
      }
    },
    StopEntries(entry) {
      const entries = entry === undefined
      ? (this.controllerGroup === 1) ? [this.Data.ID] : this.Data.runningEntries.map((entry) => entry.handleID)
      : [entry.handleID]

      this.SocketMessageSend({
        component: 'Programs',
        command: 'Stop',
        // controllergroup 1 signifies TWC
        handleID: entries
      })
    },
    PauseOrResumeEntries(Entry) { // Do all
      let entries
      let hasPausedEntries = false

      if(Entry === undefined) {
        hasPausedEntries = this.HasPausedEntries
        // controllergroup 1 signifies TWC
        entries = (this.controllerGroup === 1) ? [this.Data.ID] : this.Data.runningEntries.map((Entry) => Entry.handleID)
      } else {
        hasPausedEntries = (Entry.runningMode === 'Paused')
        entries = [Entry.handleID]
      }

      this.SocketMessageSend({
        component: 'Programs',
        command: (hasPausedEntries) ? 'Resume' : 'Pause',
        handleID: entries
      })
      this.OverrideStatusText = (hasPausedEntries) ? 'Resuming' : 'Pausing'
    },
    EditClick() {
      this.$emit('AddProgram', this.Data)
    },
    Countdown() {
      if(
        (this.Data.stStat !== undefined && this.Data.stStat !== 'Passive') ||
        (this.Data.runningEntries.length && this.Data.runningEntries[0].runningMode === 'Soaking')) {
        return 0
      } else {
        return (339.292 - ((339.292 / this.Granularity)) * this.TimeInPercentage)
      }
    },
    DayClicked(Day, Index) {
      // console.debug(Day)
    },
    SetLayout() {
      let $componentEl = $(this.$el)
      if(this.OptionsToggleStatus) {
        const $el = $(this.$refs.ProgramOptions)
        $componentEl.css({marginBottom: $el.outerHeight(true) + 20})
      } else {
        $componentEl.css({marginBottom: 50})
      }

      this.$emit('reloadProgramsLayout')
    },
    OptionsToggleClick(e) {
      this.OptionsToggleStatus = !this.OptionsToggleStatus
    },
    formatClock(sec) {
      const {hhString = '00', mmString = '00', ssString = '00'} = secondsToObj(sec)
      return `${hhString}:${mmString}:${ssString}`
    },
    ShowAllStations() {
      this.ShowAll = !this.ShowAll
    },
    SetValue(Message) {
    },
    DefaultOptionSelect(Selected, Data) {
      let SelectedData = {Selected: '', SelectedData: ''}
      if(Selected.Selected === '' || Selected.Selected === undefined) {
        Data.runningEntries.forEach( function(element, index) {
          if(element.runningStations.length) {
            SelectedData.Selected = element.runningStations[0].name
            SelectedData.SelectedData = element.runningStations[0]
          }
        })
      }
      this.Selected = SelectedData.Selected
      return SelectedData
    },
    Change(Selected) {
      let SelData = this.SelectedData
      this.Data.runningEntries.forEach( function(element, index) {
        element.runningStations.forEach( function(element, index) {
          if(element.name === Selected.target.value) {
            SelData.SelectedData = element
            SelData.Selected = element.name
          }
        })
      })
      this.SelectedData = SelData
    },
  }
}
</script>

<style lang="scss">
  .program {
    @include clearfix();
    $spacing: 10px;
    margin: $spacing 10px 50px;
    display: inline-block;
    position: relative;
    width: 310px;

    &__popup-menu {
      position: absolute;
    }

    &-wrapper {
      border-radius: $application-base-border-radius;
      transform: translateZ(0);
      z-index: $z-normal + 1;
      box-shadow: 0 0 6px -1px $color-grey;
      @include program-status-background-color();
      // padding-bottom: $spacing / 2;
    }

    &__controls {
      box-sizing: border-box;
      color: $color-grey;
      margin-left: 15px;
      flex: 0 0 auto;

      &__run-control {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 55px;
        height: 55px;
        margin: 0 !important;  // TODO: no important;
        padding: 0 !important;  // TODO: no important;
        font-size: 20px;
        color: $color-grey;
        box-shadow: 0px 0px 14px $color-grey;

        > .svg-inline--fa {
          @include center();
        }

        &--play {
          .svg-inline--fa {
            padding-left: 3px;
          }
        }

        &--times {
          opacity: 0.25;
          .svg-inline--fa {
            font-size: 25px;
          }
        }

        .radial-range {
          position: absolute;
          left: 1;
          top: 1;
          transform: rotateZ(-90deg) scale(1.2);
          border-radius: 50%;
          z-index: $z-ui;
          pointer-events: none;

          &__ring {
            stroke-dasharray: 339.292;
            stroke-dashoffset: 339.292;
            transition: stroke-dashoffset 0.4s;
          }
        }
      }

      &__clock {
        font-size: 11px;
        text-align: center;
        padding: 5px 0 10px;
        color: $color-grey-dark;
      }

    }
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: $spacing $spacing $spacing / 2;
    }
    &__body {
      margin: $spacing $spacing 0;
      background-color: inherit;

      &, &__days-wrapper {
        display: flex;
        align-items: stretch;
        height: 100%;

        > * {
          flex: 0 1 auto;
        }
      }

      &__days-wrapper {
        min-height: 30px;
        width: 100%;
      }

      &-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
      }

      &__information {
        margin: 5px;
        font-size: 10px;

        p {
          margin: 5px;
        }

        &__station-icon {
          display: inline-block;
          width: 15px;
        }
      }

      &__intelligence-wrapper {
        position: relative;
        left: 6px;
      }
      &__intelligence-levels {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        // opacity: 0.2;

        > * {
          flex: 0 1 auto;
        }
      }
      &__intelligence-level {
        @include program-status-background-color();
        flex: 0 1 auto;
        box-sizing: border-box;
        width: 20px;
        height: 100%;
        margin-bottom: 2px;
        background-position: 12px center;
        background-repeat: no-repeat;
        background-size: 40px;

        &:first-child {
          border-top-left-radius: $application-base-border-radius;
        }
        &:last-child {
          border-bottom-left-radius: $application-base-border-radius;
          margin-bottom: 0;
        }
      }

      &__days {
        display: flex;
        align-items: center;
        justify-content: space-around;
        width: 100%;
        border: 1px solid $color-grey-dark;
        overflow: hidden;
        border-radius: $application-base-border-radius;
        z-index: $z-normal;
        @include program-status-background-color();
        @include g-program-status-border-color();
      }
      &__day {
        display: inline-block;
        box-sizing: border-box;
        margin: 4px;
        text-align: center;
        font-family: $font-primary-semibold;
        color: $color-grey-dark;
        cursor: pointer;

        &__circle {
          // hack to make proportional circle
          height: 18px;
          width: 18px;
          // hack end
          background: $color-white;
          border-radius: 50%;
          border: 1px solid $color-program-active--strong;
          @include program-status-background-color();
          @include g-program-status-border-color();
        }
      }
    }

    &__edit-button {
      flex: 0 0 auto;
      font-size: 11px;
      padding: 2px 10px !important; // TODO: no important
      background-color: rgba($color-white, 0.5);
      margin: 0 !important;  // TODO: no important
      text-transform: uppercase;

      &--disabled {
        opacity: 0.5;
      }
    }

    &__name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-transform: uppercase;
      font-family: $font-primary-semibold;
      font-weight: 100;
      font-size: 15px;
      padding: 2px;
      margin: 0 25px 0 0;
    }
    &__options {
      @include optionsPanel();
      margin-top: -$application-base-border-radius;
      &-wrapper {
        border-bottom-left-radius: $application-base-border-radius;
        border-bottom-right-radius: $application-base-border-radius;
        padding-bottom: 10px;
      }

      p {
        margin: 10px;
      }

      &__option {
        min-height: 100px;
        display: flex;
        // align-items: center;
        margin: 0 $spacing 0;

        progress {
          &::-moz-progress-bar      { background: $color-program-running--strong; }
          &::-webkit-progress-value { background: $color-program-running--strong; }
          progress                  { color: $color-program-running--strong;      }
        }

        &:not(:first-child) {
          border-top: 1px solid $color-grey;
          padding-top: $spacing;
        }
        &__information {
          display: flex;
          flex-direction: column;
          // justify-content: space-between;
          width: 100%;
          margin-right: 5px;
        }
        &__entry {
          display: flex;
          align-items: center;
          justify-content: space-between;
          &:not(:first-child) {
            margin-top: $spacing;
          }
          &:last-child {
            padding-bottom: 10px;
          }
          &__stationlist {
            margin-bottom: 0;

            select {
              font-size: 11px;
            }
          }
        }
      }
    }
  }
</style>
